// components/achievements/AchievementCelebration.jsx
// Global Achievement Celebration Popup
// ============================================
// This component renders at the app root level and shows
// celebration popups whenever achievements are earned

import React, { useEffect, useState, useRef } from 'react';
import { Coins, Share2, X, ChevronRight } from 'lucide-react';
import { useAchievements } from '../../context/AchievementContext';

// Confetti particle component
const Confetti = ({ color, left, delay }) => (
  <div
    className="absolute w-3 h-3 opacity-0"
    style={{
      left: `${left}%`,
      top: '-10px',
      backgroundColor: color,
      animation: `confetti-fall 2.5s ease-out ${delay}s forwards`,
      transform: `rotate(${Math.random() * 360}deg)`,
      borderRadius: Math.random() > 0.5 ? '50%' : '0%',
    }}
  />
);

// Generate confetti particles
const generateConfetti = (count = 50) => {
  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96E6A1', '#DDA0DD', '#F7DC6F'];
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    color: colors[Math.floor(Math.random() * colors.length)],
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
  }));
};

const AchievementCelebration = () => {
  const { currentAchievement, isOpen, dismissCurrent, queueLength } = useAchievements();
  const [confetti, setConfetti] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const audioRef = useRef(null);

  // Generate confetti when achievement appears
  useEffect(() => {
    if (isOpen && currentAchievement) {
      setConfetti(generateConfetti(60));
      setIsAnimating(true);
      
      // Play celebration sound (optional - uncomment if you add sound file)
      // if (audioRef.current) {
      //   audioRef.current.currentTime = 0;
      //   audioRef.current.play().catch(() => {});
      // }
      
      // Reset animation state after animation completes
      const timer = setTimeout(() => setIsAnimating(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentAchievement]);

  // Handle share
  const handleShare = async () => {
    if (!currentAchievement) return;
    
    const shareText = `🏆 I just earned the "${currentAchievement.name}" achievement on UniLedge! +${currentAchievement.tokens} tokens 🎉\n\nJoin me and start saving: https://uniledge.io`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Achievement Unlocked!',
          text: shareText,
          url: 'https://uniledge.io'
        });
      } catch (err) {
        // User cancelled or error
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareText);
      alert('Copied to clipboard!');
    }
  };

  if (!isOpen || !currentAchievement) return null;

  return (
    <>
      {/* Celebration sound (optional) */}
      {/* <audio ref={audioRef} src="/sounds/achievement.mp3" preload="auto" /> */}
      
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={dismissCurrent}
      >
        {/* Confetti Container */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {isAnimating && confetti.map(c => (
            <Confetti key={c.id} {...c} />
          ))}
        </div>
        
        {/* Achievement Card */}
        <div 
          className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl relative"
          onClick={e => e.stopPropagation()}
          style={{ animation: 'achievement-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
        >
          {/* Close button */}
          <button 
            onClick={dismissCurrent}
            className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 z-10"
          >
            <X size={20} />
          </button>
          
          {/* Gradient Header */}
          <div className="bg-gradient-to-br from-yellow-400 via-orange-500 to-pink-500 pt-8 pb-12 px-6 text-center relative overflow-hidden">
            {/* Sparkle effects */}
            <div className="absolute inset-0">
              <div className="absolute top-4 left-8 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDuration: '1.5s' }} />
              <div className="absolute top-12 right-12 w-1.5 h-1.5 bg-white rounded-full animate-ping" style={{ animationDuration: '2s', animationDelay: '0.3s' }} />
              <div className="absolute bottom-8 left-16 w-1 h-1 bg-white rounded-full animate-ping" style={{ animationDuration: '1.8s', animationDelay: '0.6s' }} />
              <div className="absolute bottom-4 right-8 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDuration: '2.2s', animationDelay: '0.2s' }} />
            </div>
            
            <p className="text-white/90 text-sm font-medium mb-2 uppercase tracking-wider">
              🎉 Achievement Unlocked!
            </p>
            
            {/* Achievement Icon */}
            <div 
              className="text-7xl mb-3"
              style={{ animation: 'icon-bounce 0.6s ease-out 0.2s both' }}
            >
              {currentAchievement.icon}
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-1">
              {currentAchievement.name}
            </h2>
          </div>
          
          {/* Content */}
          <div className="px-6 py-6 text-center -mt-6">
            {/* Token Reward Badge */}
            <div 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-300 text-yellow-800 rounded-full px-5 py-2.5 mb-4 shadow-lg"
              style={{ animation: 'token-pop 0.4s ease-out 0.4s both' }}
            >
              <Coins className="w-6 h-6" />
              <span className="text-xl font-bold">+{currentAchievement.tokens}</span>
              <span className="font-medium">tokens</span>
            </div>
            
            {/* Description */}
            <p className="text-gray-600 mb-6">
              {currentAchievement.description}
            </p>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleShare}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              >
                <Share2 size={18} />
                Share
              </button>
              <button
                onClick={dismissCurrent}
                className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
              >
                {queueLength > 0 ? (
                  <>
                    Next
                    <ChevronRight size={18} />
                    <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {queueLength}
                    </span>
                  </>
                ) : (
                  'Awesome!'
                )}
              </button>
            </div>
          </div>
          
          {/* Queue indicator */}
          {queueLength > 0 && (
            <div className="bg-orange-50 border-t border-orange-100 px-6 py-3 text-center">
              <p className="text-sm text-orange-700">
                🎁 {queueLength} more achievement{queueLength > 1 ? 's' : ''} waiting!
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Keyframe Animations */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            opacity: 1;
            transform: translateY(0) rotate(0deg);
          }
          100% {
            opacity: 0;
            transform: translateY(100vh) rotate(720deg);
          }
        }
        
        @keyframes achievement-pop {
          0% {
            opacity: 0;
            transform: scale(0.5) translateY(20px);
          }
          50% {
            transform: scale(1.05) translateY(0);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes icon-bounce {
          0% {
            opacity: 0;
            transform: scale(0) rotate(-20deg);
          }
          50% {
            transform: scale(1.2) rotate(10deg);
          }
          75% {
            transform: scale(0.9) rotate(-5deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        
        @keyframes token-pop {
          0% {
            opacity: 0;
            transform: scale(0) translateY(10px);
          }
          60% {
            transform: scale(1.1) translateY(0);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default AchievementCelebration;